/**
 * The filter controls for a screener table.
 *
 * Numbers get an optional min/max; finite categories get a checklist built
 * from the values actually present; the code gets a substring box because a
 * thousand codes is not a checklist.
 *
 * Split out of the table so the table stays about rows, and so the controls
 * can be laid out as their own grid — on a phone they stack, and a cramped
 * filter is a filter nobody uses.
 */
import {
	categoryOptions,
	type ColumnSpec,
	type FilterState,
	type NumericRange,
} from "../domain/screenerTable.ts";
import type { ScreenerRow } from "../api/screener.ts";

interface ScreenerFiltersProps {
	columns: readonly ColumnSpec[];
	/** Every fetched row — the checklist options come from these, not from
	 *  what is currently visible, or unchecking a box could not bring it back. */
	rows: readonly ScreenerRow[];
	filters: FilterState;
	onChange: (filters: FilterState) => void;
	onClear: () => void;
	unfiltered: boolean;
	shown: number;
	total: number;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/** "" means "no bound", which is different from 0. */
function parseBound(raw: string): number | undefined {
	const trimmed = raw.trim();
	if (!trimmed) return undefined;
	const value = Number(trimmed);
	return Number.isFinite(value) ? value : undefined;
}

export function ScreenerFilters({
	columns,
	rows,
	filters,
	onChange,
	onClear,
	unfiltered,
	shown,
	total,
	open,
	onOpenChange,
}: ScreenerFiltersProps) {
	const setRange = (columnId: string, edge: keyof NumericRange, raw: string) => {
		const range = { ...(filters.ranges[columnId] ?? {}), [edge]: parseBound(raw) };
		onChange({ ...filters, ranges: { ...filters.ranges, [columnId]: range } });
	};

	const toggleCategory = (columnId: string, option: string) => {
		const selected = filters.categories[columnId] ?? [];
		const next = selected.includes(option)
			? selected.filter((entry) => entry !== option)
			: [...selected, option];
		onChange({ ...filters, categories: { ...filters.categories, [columnId]: next } });
	};

	return (
		<section className="filters" aria-labelledby="filters-heading">
			<div className="filters__bar">
				<button
					type="button"
					className="filters__toggle"
					aria-expanded={open}
					aria-controls="screener-filters"
					onClick={() => onOpenChange(!open)}
				>
					<span id="filters-heading">Filters</span>
					{unfiltered ? null : <span className="filters__badge">on</span>}
				</button>

				{/* The count is outside the collapsible panel on purpose: with the
				    panel shut, this is the only thing saying rows are hidden. */}
				<p className="filters__count" aria-live="polite">
					{shown === total
						? `${total} ${total === 1 ? "stock" : "stocks"}`
						: `${shown} of ${total} stocks shown`}
				</p>

				{unfiltered ? null : (
					<button type="button" className="filters__clear" onClick={onClear}>
						Clear filters
					</button>
				)}
			</div>

			<div id="screener-filters" className="filters__panel" hidden={!open}>
				{columns.map((column) => {
					if (column.kind === "numeric") {
						const range = filters.ranges[column.id] ?? {};
						return (
							<fieldset key={column.id} className="filters__group">
								<legend className="filters__legend">{column.filterLabel}</legend>
								<div className="filters__range">
									<label className="filters__field">
										<span className="visually-hidden">{column.filterLabel} minimum</span>
										<input
											type="number"
											className="filters__input"
											placeholder="min"
											value={range.min ?? ""}
											onChange={(event) => setRange(column.id, "min", event.target.value)}
										/>
									</label>
									<span aria-hidden="true" className="filters__dash">
										–
									</span>
									<label className="filters__field">
										<span className="visually-hidden">{column.filterLabel} maximum</span>
										<input
											type="number"
											className="filters__input"
											placeholder="max"
											value={range.max ?? ""}
											onChange={(event) => setRange(column.id, "max", event.target.value)}
										/>
									</label>
								</div>
							</fieldset>
						);
					}

					if (column.kind === "category") {
						const options = categoryOptions(rows, column.id);
						if (options.length === 0) return null;
						const selected = filters.categories[column.id] ?? [];
						return (
							<fieldset key={column.id} className="filters__group">
								<legend className="filters__legend">{column.filterLabel}</legend>
								<div className="filters__checklist">
									{options.map((option) => (
										<label key={option} className="filters__check">
											<input
												type="checkbox"
												checked={selected.includes(option)}
												onChange={() => toggleCategory(column.id, option)}
											/>
											<span>{option}</span>
										</label>
									))}
								</div>
							</fieldset>
						);
					}

					return (
						<fieldset key={column.id} className="filters__group">
							<legend className="filters__legend">{column.filterLabel}</legend>
							<label className="filters__field">
								<span className="visually-hidden">Filter by {column.filterLabel}</span>
								<input
									type="search"
									className="filters__input filters__input--text"
									placeholder="contains…"
									value={filters.text[column.id] ?? ""}
									onChange={(event) =>
										onChange({ ...filters, text: { ...filters.text, [column.id]: event.target.value } })
									}
								/>
							</label>
						</fieldset>
					);
				})}

				<p className="filters__note">
					A row with no value for a filtered column is hidden by that filter: “at least 0” cannot be
					answered for a stock the API had no number for.
				</p>
			</div>
		</section>
	);
}
